(function (root) {
  'use strict';

  if (root.EcomAnalytics) return;

  const config = root.ecomAnalyticsConfig || {};
  const availabilitySignatures = new WeakMap();
  const pendingAtcSources = new WeakMap();
  const pageType = normalizeToken(config.pageType, 'unknown');
  const templateSuffix = normalizeToken(config.templateSuffix, 'none');
  const schemaVersion = '1';

  function normalizeToken(value, fallback) {
    const normalized = String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 80);
    return normalized || fallback;
  }

  function safeText(value, fallback) {
    const text = String(value == null ? '' : value).trim().replace(/[<>]/g, '').slice(0, 100);
    return text || fallback;
  }

  function customEventName(name) {
    return normalizeToken(name, '').slice(0, 36);
  }

  function analyticsPublisher() {
    const shopify = root.Shopify;
    return shopify && shopify.analytics && typeof shopify.analytics.publish === 'function'
      ? shopify.analytics.publish.bind(shopify.analytics)
      : null;
  }

  function sanitizeItems(items) {
    if (!Array.isArray(items)) return [];
    return items.slice(0, 10).map((item) => {
      if (!item || typeof item !== 'object') return null;
      const normalized = {};
      ['item_id', 'item_name', 'item_variant', 'item_list_id', 'item_list_name'].forEach((key) => {
        if (item[key] != null && String(item[key]).trim()) normalized[key] = safeText(item[key], 'unknown');
      });
      if (Number.isFinite(Number(item.price))) normalized.price = Number(item.price);
      if (Number.isFinite(Number(item.quantity))) normalized.quantity = Number(item.quantity);
      return normalized.item_id || normalized.item_name ? normalized : null;
    }).filter(Boolean);
  }

  function sanitizePayload(payload, ga4Name) {
    const sanitized = {
      ga4_event_name: ga4Name,
      ecom_schema_version: schemaVersion,
      ecom_page_type: pageType,
      ecom_template_suffix: templateSuffix
    };

    Object.entries(payload || {}).forEach(([key, value]) => {
      if (!/^[a-z][a-z0-9_]{0,39}$/.test(key) || value == null) return;
      if (key === 'items') {
        const items = sanitizeItems(value);
        if (items.length) sanitized.items = items;
        return;
      }
      if (typeof value === 'number' && Number.isFinite(value)) {
        sanitized[key] = value;
        return;
      }
      if (typeof value === 'boolean') {
        sanitized[key] = value ? 'true' : 'false';
        return;
      }
      if (typeof value === 'string' && value.trim()) {
        sanitized[key] = key.endsWith('_name') ? safeText(value, 'unknown') : normalizeToken(value, 'unknown');
      }
    });
    return sanitized;
  }

  function publish(name, payload, ga4Name) {
    const publisher = analyticsPublisher();
    const normalizedName = customEventName(name);
    const resolvedGa4Name = ga4Name || `ecom_${normalizedName}`;
    if (!publisher || !normalizedName || !/^[a-z][a-z0-9_]{0,39}$/.test(resolvedGa4Name)) return false;

    try {
      publisher(`ecom:${normalizedName}`, sanitizePayload(payload, resolvedGa4Name));
      return true;
    } catch (error) {
      return false;
    }
  }

  function trackTool(tool, action) {
    const toolId = normalizeToken(tool, 'unknown');
    const normalizedAction = normalizeToken(action, 'unknown');
    if (normalizedAction === 'start') {
      return publish('tool_start', { ecom_tool_id: toolId });
    }
    if (normalizedAction === 'result' || normalizedAction === 'calculate') {
      return publish('tool_complete', { ecom_tool_id: toolId });
    }
    return publish('tool_action', { ecom_tool_id: toolId, ecom_action: normalizedAction });
  }

  function sourceComponent(node) {
    if (!node) return 'unknown';
    const explicit = node.closest('[data-ecom-source-component]');
    if (explicit && explicit.dataset.ecomSourceComponent) {
      return normalizeToken(explicit.dataset.ecomSourceComponent, 'unknown');
    }
    const text = `${node.className || ''} ${node.closest('form, product-form')?.className || ''}`.toLowerCase();
    if (text.includes('sticky')) return 'sticky_atc';
    if (text.includes('quick-add') || text.includes('quick_add')) return 'collection_quick_add';
    if (text.includes('recommend')) return 'recommendation_card';
    if (text.includes('upsell')) return 'cart_upsell';
    if (text.includes('article')) return 'article_cta';
    if (text.includes('tool')) return 'tool_recommendation';
    if (text.includes('main-product')) return 'main_product_form';
    return 'unknown';
  }

  function availabilityState(node) {
    return node?.dataset?.availabilityState || node?.dataset?.ecomAvailabilityState || 'unknown';
  }

  function emitAvailability(node) {
    if (!node || !availabilityState(node) || availabilityState(node) === 'unknown') return;
    const displayed = normalizeToken(availabilityState(node), 'unknown');
    const stateButton = node.matches && node.matches('button')
      ? node
      : node.querySelector?.('button.atc-button, button[data-availability-state]');
    const isButton = Boolean(stateButton);
    const buttonState = isButton ? (stateButton.disabled ? 'disabled' : 'enabled') : 'visible';
    const actual = node.dataset.ecomActualAvailability || (isButton && stateButton.disabled ? 'unavailable' : 'available');
    const conflict = displayed === 'sold_out' && actual === 'available'
      ? 'sold_out_label_but_available'
      : displayed !== 'sold_out' && actual === 'unavailable'
        ? 'displayed_available_but_unavailable'
        : 'none';
    const signature = [displayed, actual, buttonState, sourceComponent(node)].join('|');
    if (availabilitySignatures.get(node) === signature) return;
    availabilitySignatures.set(node, signature);
    publish('availability_view', {
      ecom_displayed_state: displayed,
      ecom_actual_state: normalizeToken(actual, 'unknown'),
      ecom_button_state: buttonState,
      ecom_availability_conflict: conflict,
      ecom_source_component: sourceComponent(node)
    });
  }

  function scanAvailability() {
    root.document.querySelectorAll('[data-availability-state], [data-ecom-availability-state]').forEach(emitAvailability);
  }

  function classifyAtcError(text) {
    const normalized = String(text || '').toLowerCase();
    if (/sold|stock|unavailable|quantity|inventory/.test(normalized)) return 'out_of_stock_or_quantity';
    if (/required|select|enter|personaliz/.test(normalized)) return 'validation';
    if (/network|server|try again|error|failed/.test(normalized)) return 'network_or_api';
    return 'unknown';
  }

  function observeAtcError(form) {
    const scope = form.closest('product-form') || form;
    if (typeof root.MutationObserver !== 'function') return;
    const observer = new root.MutationObserver(() => {
      const messageNode = Array.from(scope.querySelectorAll('.product-form__error-message, .main-atc__error, [role="alert"]'))
        .find((node) => !node.hidden && String(node.textContent || '').trim());
      if (!messageNode) return;
      publish('atc_error', {
        ecom_error_reason: classifyAtcError(messageNode.textContent),
        ecom_source_component: sourceComponent(form)
      });
      observer.disconnect();
    });
    observer.observe(scope, { subtree: true, childList: true, attributes: true, attributeFilter: ['hidden', 'style', 'class'] });
    root.setTimeout(() => observer.disconnect(), 8000);
  }

  function trackAtcAttempt(form) {
    const button = form.querySelector('button[name="add"], button[type="submit"]');
    const productForm = form.closest('product-form');
    const variantInput = form.querySelector('[name="id"]');
    const availabilityNode = productForm?.querySelector('[data-availability-state]') || button;
    publish('atc_attempt', {
      ecom_source_component: pendingAtcSources.get(form) || sourceComponent(button || form),
      ecom_selected_variant: variantInput?.value || 'unknown',
      ecom_availability_state: availabilityState(availabilityNode)
    });
    pendingAtcSources.delete(form);
    observeAtcError(form);
  }

  function trackProductSelection(card) {
    const itemId = card.dataset.ecomItemId;
    if (!itemId) return;
    publish('select_item', {
      item_list_id: normalizeToken(root.location.pathname, 'unknown'),
      item_list_name: pageType,
      items: [{
        item_id: itemId,
        item_name: card.dataset.ecomItemName || 'unknown',
        item_variant: card.dataset.ecomItemVariant || 'unknown',
        price: card.dataset.ecomItemPrice
      }]
    }, 'select_item');
  }

  function init() {
    root.document.addEventListener('submit', (event) => {
      const form = event.target.closest && event.target.closest('form[data-type="add-to-cart-form"]');
      if (form) trackAtcAttempt(form);
    }, true);

    root.document.addEventListener('click', (event) => {
      const stickyConfirm = event.target.closest && event.target.closest('.sticky-atc-confirm');
      if (stickyConfirm) {
        const form = root.document.querySelector('product-form.main-product-form form[data-type="add-to-cart-form"]');
        if (form) pendingAtcSources.set(form, 'sticky_atc');
      }

      const filter = event.target.closest && event.target.closest('[data-tool-filter]');
      if (filter) publish('tool_directory_filter', { ecom_filter: filter.dataset.toolFilter || 'unknown' });

      const summary = event.target.closest && event.target.closest('summary');
      if (summary && summary.closest('details') && /faq/i.test(summary.closest('details').className || '')) {
        publish('faq_open', { ecom_faq_group: pageType });
      }

      const card = event.target.closest && event.target.closest('[data-ecom-product-card]');
      if (card && event.target.closest('a[href]') && !event.target.closest('button, form')) trackProductSelection(card);
    });

    root.document.addEventListener('change', (event) => {
      const target = event.target;
      if (target.matches && target.matches('variant-radios input, variant-selects select, [name^="options["]')) {
        const form = target.closest('product-info, product-form')?.querySelector('form[data-type="add-to-cart-form"]');
        publish('variant_select', {
          ecom_option_name: target.name || target.dataset.optionName || 'unknown',
          ecom_option_value: target.value || 'unknown',
          ecom_selected_variant: form?.querySelector('[name="id"]')?.value || 'unknown',
          ecom_source_component: sourceComponent(target)
        });
        root.setTimeout(scanAvailability, 0);
      }
    });

    root.addEventListener('vep:pet-tool-action', (event) => {
      const detail = event.detail || {};
      trackTool(detail.tool, detail.action);
    });

    root.document.addEventListener('shopify:section:load', scanAvailability);
    scanAvailability();

    if (/^(article|blog)$/.test(pageType) || root.location.pathname.includes('/blogs/')) {
      let maxScroll = 0;
      let sent = false;
      const onScroll = () => {
        const documentHeight = root.document.documentElement.scrollHeight || 1;
        maxScroll = Math.max(maxScroll, (root.scrollY + root.innerHeight) / documentHeight);
        if (!sent && maxScroll >= 0.5) {
          sent = true;
          publish('content_engaged', { ecom_content_type: pageType, ecom_engagement_threshold: '30s_50pct' });
        }
      };
      root.addEventListener('scroll', onScroll, { passive: true });
      root.setTimeout(() => {
        if (root.document.visibilityState !== 'hidden') onScroll();
      }, 30000);
    }
  }

  root.EcomAnalytics = Object.freeze({ publish, scanAvailability, trackTool });
  if (root.document.readyState === 'loading') {
    root.document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
}(window));
